import React, { useState, useContext } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import useStyles from 'hooks/useStyles';
import { bool } from 'prop-types';

import ApplicationContext from 'components/ApplicationContext';
import CourseContext from 'routes/course/components/CourseContext';

import { getLessonEventTrackData } from 'routes/course/hooks/useLessonEventTrack';

import { sendGTMEvent } from 'reduxModules/googleTagManager/action';

import {
  COURSE_LESSONPAGE_MATERIAL_CLICK,
  COURSE_MATERIAL_CLICK,
} from 'constants/gtmEvent';
import { LESSON } from 'constants/courseEventSections';

import LessonListItem from '../LessonListItem';
import PlayListHeader from './components/PlayListHeader';
import CoursePlayListForFreeUser from '../CoursePlayListForFreeUser';
import TrailerLessonItem from '../TrailerLessonItem';
import LessonUnitHeader from '../LessonUnitHeader';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './PlayList.scss';

function PlayList({ isLearningPage, isLessonPage }) {
  useStyles(styles);

  const {
    query: { freeLesson },
  } = useContext(ApplicationContext);

  const {
    hasPreviewLesson,
    onLessonListPopupModeClose,
    playerRef,
    currentLesson,
    onClickLesson,
    onClickLessonWrapper,
    courseEventTrackData,
    userPurchased,
    lessonList,
    lessonListIsPopupMode,
  } = useContext(CourseContext);

  const dispatch = useDispatch();

  const [showFreeLesson, setShowFreeLesson] = useState(freeLesson === 'true');

  const renderLessonListItem = (lesson, index) => {
    const isSelected = currentLesson.id === lesson.id;

    const handleMaterialClick = ({ id, title }) => {
      if (isLessonPage) {
        dispatch(
          sendGTMEvent({
            eventName: COURSE_LESSONPAGE_MATERIAL_CLICK,
            data: {
              courseId: courseEventTrackData.courseId,
              courseName: courseEventTrackData.courseName,
              freeLesson: lesson.isFree,
              lessonId: lesson.id,
              lessonName: lesson.name,
              materialId: id,
              materialName: title,
              section: LESSON,
            },
          }),
        );
      } else {
        dispatch(
          sendGTMEvent({
            eventName: COURSE_MATERIAL_CLICK,
            data: {
              materialId: id,
              materialName: title,
              section: 'course_player_lessons_tab',
              ...courseEventTrackData,
              ...getLessonEventTrackData({
                lesson,
              }),
            },
          }),
        );
      }
    };

    return (
      <LessonListItem
        key={lesson.id}
        {...lesson}
        isChapterListOpen={currentLesson.isChapterListOpen}
        isPlaying={isSelected && currentLesson.isPlaying}
        isSelected={isSelected}
        onLessonClick={() => onClickLesson(lesson, index)}
        onLessonWrapperClick={() => onClickLessonWrapper(lesson, index)}
        onMaterialClick={handleMaterialClick}
        playerRef={playerRef}
        hasChapters={lesson.chapters.length !== 0}
      />
    );
  };

  const playListChildren = [
    {
      visible: true,
      name: 'LessonList',
      className: styles.lessonList,
      header: (
        <PlayListHeader
          isLearningPage={isLearningPage}
          onClose={onLessonListPopupModeClose}
          setShowFreeLesson={setShowFreeLesson}
          showFreeLesson={showFreeLesson}
        />
      ),
      content: <ul>{lessonList.map(renderLessonListItem)}</ul>,
    },
  ];

  if (!isLearningPage && !isLessonPage && !userPurchased)
    return <CoursePlayListForFreeUser />;

  return playListChildren.map(children => {
    const { visible, name, header, content, className } = children;

    if (!visible) {
      return null;
    }
    return (
      <div
        className={cx(styles.playlistChildWrapper, className, {
          [styles.fixed]: !isLearningPage,
          [styles.learningStatus]: isLearningPage,
        })}
        key={name}
      >
        <LessonUnitHeader
          show={
            !isLessonPage &&
            !isLearningPage &&
            (lessonListIsPopupMode || (!hasPreviewLesson && !userPurchased))
          }
        />
        <TrailerLessonItem show={!isLearningPage && !isLessonPage} />
        <div
          className={cx(styles.meta, {
            [styles.chapterListHeader]: name === 'ChapterList',
          })}
        >
          {header}
        </div>

        <div className={styles.container}>
          <div className={styles.inner}>{content}</div>
        </div>
      </div>
    );
  });
}

PlayList.propTypes = {
  isLearningPage: bool.isRequired,
  isLessonPage: bool,
};

PlayList.defaultProps = {
  isLessonPage: false,
};

export default PlayList;
